import { useEffect, useState } from 'react'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/store/authStore'

/* Supprimer une fiche qui porte un compte UP-SPOT (01/10/2026).
 *
 * Deux choix, réservés aux administrateurs d'UP-CRM :
 *   - « Désactiver le compte » (par défaut) : la personne ne peut plus se
 *     connecter, ses réservations sont gardées ;
 *   - « Tout supprimer » : le compte et toutes ses réservations sont effacés.
 *     Le nombre de réservations est annoncé, et il faut cocher une case de
 *     confirmation pour que la suppression soit possible.
 *
 * Le même choix sert, depuis le 02/10/2026, au bouton « Désactiver le compte »
 * de la fenêtre « Modifier » (`ficheConservee`) : la fiche contact y est
 * toujours gardée.
 */

export type ChoixCompteUpSpot = 'desactiver' | 'supprimer'

interface Props {
  contactId: number
  choix: ChoixCompteUpSpot
  onChoix: (choix: ChoixCompteUpSpot) => void
  confirme: boolean
  onConfirme: (confirme: boolean) => void
  /** Vrai depuis « Modifier » : on règle le compte, la fiche reste. */
  ficheConservee?: boolean
}

/** Vrai si la suppression peut partir avec ce choix. */
export const suppressionPossible = (estAdmin: boolean, choix: ChoixCompteUpSpot, confirme: boolean) =>
  estAdmin && (choix === 'desactiver' || confirme)

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`

export default function ChoixSuppressionUpSpot({ contactId, choix, onChoix, confirme, onConfirme, ficheConservee = false }: Props) {
  const estAdmin = Boolean(useAuthStore((etat) => etat.user?.is_admin))
  const [decompte, setDecompte] = useState<{ total: number; aVenir: number } | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    if (!estAdmin) return
    let actif = true
    setDecompte(null)
    setErreur(null)
    apiService.decompteReservationsUpSpot(contactId).then((reponse) => {
      if (!actif) return
      if (reponse.success && reponse.data) setDecompte(reponse.data)
      else setErreur(reponse.error || 'Le nombre de réservations n’a pas pu être lu.')
    })
    return () => {
      actif = false
    }
  }, [contactId, estAdmin])

  if (!estAdmin) {
    return (
      <p className="text-sm text-red-700">
        Cette fiche porte un compte UP-SPOT : seul un administrateur peut la supprimer.
      </p>
    )
  }

  const reservations = decompte
    ? decompte.total === 0
      ? 'Ce compte n’a aucune réservation.'
      : `Ce compte a ${pluriel(decompte.total, 'réservation')}` +
        (decompte.aVenir > 0 ? `, dont ${decompte.aVenir} à venir.` : ', toutes passées.')
    : erreur ?? 'Lecture du nombre de réservations…'

  const option = (valeur: ChoixCompteUpSpot, titre: string, texte: string) => (
    <label
      className={`flex gap-3 rounded-xl border p-3 cursor-pointer ${
        choix === valeur ? (valeur === 'supprimer' ? 'border-red-400 bg-red-50' : 'border-violet-400 bg-violet-50') : 'border-gray-200'
      }`}
    >
      <input
        type="radio"
        name={`choix-upspot-${contactId}`}
        className="mt-1 h-4 w-4 accent-violet-600"
        checked={choix === valeur}
        onChange={() => {
          onChoix(valeur)
          onConfirme(false)
        }}
      />
      <span className="text-sm">
        <span className="block font-semibold text-gray-900">{titre}</span>
        <span className="block text-gray-600">{texte}</span>
      </span>
    </label>
  )

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-900">
        {ficheConservee ? 'Que faut-il faire du compte UP-SPOT ?' : 'Cette fiche porte un compte UP-SPOT. Que faut-il en faire ?'}
      </p>
      <p className="text-sm text-gray-600">{reservations}</p>
      {option(
        'desactiver',
        'Désactiver le compte (recommandé)',
        'La personne ne pourra plus se connecter. Ses réservations sont conservées dans l’historique.',
      )}
      {option(
        'supprimer',
        'Tout supprimer',
        'Le compte et toutes ses réservations sont effacés. Les réservations à venir sont d’abord annulées : elles disparaissent de l’agenda Outlook de la ressource et ses gestionnaires sont prévenus.'
          + (ficheConservee ? ' La fiche contact est conservée : on pourra lui rouvrir un compte plus tard.' : ''),
      )}
      {choix === 'supprimer' && (
        <label className="flex items-start gap-2 text-sm text-red-700 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-red-600"
            checked={confirme}
            onChange={(e) => onConfirme(e.target.checked)}
          />
          <span>
            Je confirme vouloir effacer définitivement le compte
            {decompte && decompte.total === 1 ? ' et sa réservation' : ''}
            {decompte && decompte.total > 1 ? ` et ses ${decompte.total} réservations` : ''}.
          </span>
        </label>
      )}
    </div>
  )
}
